import React, { useCallback, useState } from 'react';
import { Button, SelectChangeEvent } from '@mui/material';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import styled from 'styled-components';

import CollapseExpandSections from 'components/CollapseExpandSections';
import ControlledTextField from 'components/ControlledTextField';
import TemplateTypePicker from 'components/TemplateTypePicker';
import Paper from 'components/Paper';
import { Action } from 'state';
import { NewTemplate } from 'types/Template';
import TemplateType from 'types/TemplateType';
import SectionForm from './SectionForm';
import { ChangeEvent } from 'utils/ui';

const StyledPaper = styled(Paper)`
  & > *:not(:last-child) {
    margin-bottom: 1rem;
  }
`;

const StyledSmallPaper = styled(Paper)`
  padding: 0.5rem;

  & > *:not(:last-child) {
    margin-bottom: 0.5rem;
  }
`;

const StyledPickers = styled.div`
  display: flex;
  flex-wrap: no-wrap;

  & > *:not(:last-child) {
    margin-right: 2rem;
  }
`;

interface TemplateFormProps {
  template: NewTemplate;
  dispatch: (action: Action) => void;
}

const TemplateForm: React.FC<TemplateFormProps> = ({ template, dispatch }) => {
  const [collapsedSections, setCollapsedSections] = useState(false);

  const handleOnChangeName = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { value: name } = e.target;
      dispatch({ type: 'set-template-name', name });
    },
    [dispatch],
  );

  const handleOnChangeReferenceLabel = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { value: referenceLabel } = e.target;
      dispatch({ type: 'set-template-reference-label', referenceLabel });
    },
    [dispatch],
  );

  const handleOnChangeUserRevision = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const userRevision: number = +e.target.value;
      dispatch({ type: 'set-template-user-revision', userRevision });
    },
    [dispatch],
  );

  const handleOnChangeTemplateType = useCallback(
    (e: ChangeEvent) => {
      const templateType = e.target.value as TemplateType;
      dispatch({ type: 'set-template-type', templateType });
    },
    [dispatch],
  );

  const handleOnClickAddSection = useCallback(() => {
    dispatch({ type: 'add-section' });
  }, [dispatch]);

  const onDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) {
        return;
      }

      const { index: destinationIndex } = result.destination;
      const { index: sourceIndex } = result.source;

      const offset = destinationIndex - sourceIndex;

      if (offset > 0) {
        dispatch({ type: 'move-section-down', sectionUuid: result.draggableId, offset });
      } else if (offset < 0) {
        dispatch({ type: 'move-section-up', sectionUuid: result.draggableId, offset: -offset });
      }
    },
    [dispatch],
  );

  const creditsSection = template.exam_sections.find((section) => section.credits);

  const handleOnToggleSections = useCallback(
    (collapsed: boolean) => {
      setCollapsedSections(collapsed);
    },
    [setCollapsedSections],
  );

  return (
    <div>
      <StyledPaper>
        <ControlledTextField
          label="Template Name"
          value={template.name}
          variant="outlined"
          size="small"
          fullWidth
          onChange={handleOnChangeName}
          style={{ marginBottom: '1rem' }}
        />
        <ControlledTextField
          label="Reference Label"
          value={template.reference_label}
          variant="outlined"
          size="small"
          fullWidth
          onChange={handleOnChangeReferenceLabel}
          style={{ marginBottom: '1rem' }}
        />
        <ControlledTextField
          label="User Revision"
          type="number"
          value={template.user_revision}
          slotProps={{ input: { inputProps: { min: 1 } } }}
          variant="outlined"
          size="small"
          onChange={handleOnChangeUserRevision}
          style={{ marginBottom: '1rem' }}
        />
        <StyledPickers>
          <TemplateTypePicker value={template.type} onChange={handleOnChangeTemplateType} />
        </StyledPickers>
      </StyledPaper>

      <StyledSmallPaper>
        <CollapseExpandSections collapsed={collapsedSections} onToggle={handleOnToggleSections} />
      </StyledSmallPaper>

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="list">
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {template.exam_sections
                .filter((section) => !section.credits)
                .map((section) => (
                  <Draggable draggableId={section.uuid} key={section.uuid} index={section.sequence_index}>
                    {(provided) => (
                      <div ref={provided.innerRef} {...provided.draggableProps}>
                        <StyledPaper>
                          <SectionForm
                            section={section}
                            dispatch={dispatch}
                            collapsed={collapsedSections}
                            dragHandleProps={provided.dragHandleProps}
                            templateType={template.type}
                          />
                        </StyledPaper>
                      </div>
                    )}
                  </Draggable>
                ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {creditsSection && (
        <StyledPaper>
          <SectionForm
            section={creditsSection}
            dispatch={dispatch}
            collapsed={collapsedSections}
            templateType={template.type}
          />
        </StyledPaper>
      )}

      <StyledPaper>
        <Button type="button" fullWidth onClick={handleOnClickAddSection}>
          Add Section
        </Button>
      </StyledPaper>
    </div>
  );
};

export default TemplateForm;
